import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

/**
 * <ProtectedRoute allow={(user)=>bool}>...</ProtectedRoute>
 * - Redirects unauthenticated users to /login.
 * - If `allow` is provided, evaluates it; on false, redirects platform users to /admin and tenant users to /dashboard.
 */
export default function ProtectedRoute({ children, allow }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading || user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="protected-loading">
        <div className="text-sm text-muted-foreground">…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;

  if (allow && !allow(user)) {
    const perms = user.permissions || [];
    const platform = user.role === "SUPER_ADMIN" || perms.some((p) => p.startsWith("platform."));
    return <Navigate to={platform ? "/admin" : (user.tenant_id ? "/dashboard" : "/login")} replace />;
  }
  return children;
}

export function isPlatformUser(user) {
  if (!user) return false;
  if (user.role === "SUPER_ADMIN") return true;
  return (user.permissions || []).some((p) => p.startsWith("platform."));
}

export function isTenantUser(user) {
  return !!user?.tenant_id;
}
